import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Package, Calendar } from "lucide-react";
import {
  entregaService,
  Entrega,
} from "../../../services/entregaService";
import { BrazilDate } from "../../../utils/BrazilDate";

interface EntregasRealizadasHojeProps {
  lojaId: number | undefined;
}

interface PedidoBadge {
  displayId: number;
  externo: boolean;
  quantidadePizzas?: number;
}

interface EntregadorPedidos {
  entregadorId: number;
  nome: string;
  totalEntregas: number;
  totalPizzasExternas: number;
  pedidos: PedidoBadge[];
}

function pedidosFromEntrega(entrega: Entrega): PedidoBadge[] {
  if (entrega.entregaExterna) {
    const detalhes = entrega.pedidosExternosDetalhes ?? [];
    if (detalhes.length > 0) {
      return detalhes.map((item) => ({
        displayId: item.displayId,
        externo: true,
        quantidadePizzas: item.quantidadePizzas,
      }));
    }
    return (entrega.pedidosDisplayIds ?? []).map((displayId) => ({
      displayId,
      externo: true,
    }));
  }

  return (entrega.pedidosDisplayIds ?? []).map((displayId) => ({
    displayId,
    externo: false,
  }));
}

function countPizzasExternas(pedidos: PedidoBadge[]): number {
  return pedidos.reduce((acc, pedido) => {
    if (!pedido.externo) return acc;
    return acc + (pedido.quantidadePizzas ?? 1);
  }, 0);
}

function aggregatePedidosByEntregador(entregas: Entrega[]): EntregadorPedidos[] {
  const byEntregador = new Map<
    number,
    {
      nome: string;
      pedidos: PedidoBadge[];
      totalEntregas: number;
      totalPizzasExternas: number;
    }
  >();

  for (const entrega of entregas) {
    const id = entrega.entregador.id;
    const nome = entrega.entregador.nome ?? "—";
    const pedidos = pedidosFromEntrega(entrega);
    const qtdEnderecos =
      pedidos.length > 0
        ? pedidos.length
        : (entrega.quantidadeEnderecos ?? 1);

    if (!byEntregador.has(id)) {
      byEntregador.set(id, {
        nome,
        pedidos: [],
        totalEntregas: 0,
        totalPizzasExternas: 0,
      });
    }

    const row = byEntregador.get(id)!;
    row.pedidos.push(...pedidos);
    row.totalEntregas += qtdEnderecos;
    row.totalPizzasExternas += countPizzasExternas(pedidos);
  }

  return Array.from(byEntregador.entries())
    .map(([entregadorId, { nome, pedidos, totalEntregas, totalPizzasExternas }]) => ({
      entregadorId,
      nome,
      totalEntregas,
      totalPizzasExternas,
      pedidos: pedidos.sort((a, b) => a.displayId - b.displayId),
    }))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}

function formatPedidoBadge(pedido: PedidoBadge): string {
  if (!pedido.externo) return String(pedido.displayId);
  if (pedido.quantidadePizzas != null && pedido.quantidadePizzas > 0) {
    return `${pedido.displayId} (${pedido.quantidadePizzas}p)`;
  }
  return `${pedido.displayId} ext`;
}

const EntregasRealizadasHoje: React.FC<EntregasRealizadasHojeProps> = ({
  lojaId,
}) => {
  const hoje = BrazilDate.operationalTodayISO();

  const { data, isLoading } = useQuery({
    queryKey: ["entregas", "pedidos-hoje", hoje, lojaId],
    queryFn: () =>
      entregaService.getAll({
        dataInicio: hoje,
        dataFim: hoje,
        lojaId: lojaId ?? undefined,
        limit: 500,
        page: 1,
      }),
    enabled: !!hoje && !!lojaId,
  });

  const rows = useMemo(() => {
    if (!data?.data?.length) return [];
    return aggregatePedidosByEntregador(data.data);
  }, [data?.data]);

  const totaisExternos = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        enderecosExternos: acc.enderecosExternos + row.pedidos.filter((p) => p.externo).length,
        pizzasExternas: acc.pizzasExternas + row.totalPizzasExternas,
      }),
      { enderecosExternos: 0, pizzasExternas: 0 }
    );
  }, [rows]);

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="mb-4 flex items-center gap-2 text-lg font-medium text-neutral-900">
          <Package size={20} />
          Entregas realizadas hoje
        </h3>
        <div className="py-8 text-center text-neutral-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-lg font-medium text-neutral-900">
          <Package size={20} />
          Entregas realizadas hoje
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {totaisExternos.enderecosExternos > 0 && (
            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
              {totaisExternos.enderecosExternos} pedido
              {totaisExternos.enderecosExternos !== 1 ? "s" : ""} externo
              {totaisExternos.enderecosExternos !== 1 ? "s" : ""}
              {totaisExternos.pizzasExternas > 0 &&
                ` · ${totaisExternos.pizzasExternas} pizza${totaisExternos.pizzasExternas !== 1 ? "s" : ""}`}
            </span>
          )}
          <span className="flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1 text-sm text-neutral-600">
            <Calendar size={14} />
            {BrazilDate.formatBR(BrazilDate.fromISOString(hoje), false)}
          </span>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="py-8 text-center text-neutral-500">
          Nenhuma entrega registrada hoje.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[320px] border-collapse text-left">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  Entregador
                </th>
                <th className="w-24 px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  Total
                </th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  Números dos pedidos
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.entregadorId}
                  className="border-b border-neutral-100 transition-colors hover:bg-neutral-50/50"
                >
                  <td className="px-4 py-3 font-medium text-neutral-900">
                    {row.nome}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center justify-center rounded-full bg-primary/10 px-2.5 py-0.5 text-sm font-semibold tabular-nums text-primary">
                      {row.totalEntregas}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-700">
                    {row.pedidos.length > 0 ? (
                      <span className="inline-flex flex-wrap gap-1">
                        {row.pedidos.map((pedido, i) => (
                          <span
                            key={`${row.entregadorId}-${pedido.displayId}-${pedido.externo}-${i}`}
                            title={
                              pedido.externo
                                ? pedido.quantidadePizzas
                                  ? `Pedido externo · ${pedido.quantidadePizzas} pizza(s)`
                                  : "Pedido externo (fora do Cardápio Web)"
                                : "Pedido da integração"
                            }
                            className={`rounded px-1.5 py-0.5 text-sm tabular-nums ${
                              pedido.externo
                                ? "bg-amber-100 font-medium text-amber-900 ring-1 ring-amber-200"
                                : "bg-neutral-100 text-neutral-800"
                            }`}
                          >
                            {formatPedidoBadge(pedido)}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EntregasRealizadasHoje;
